import { format, parse } from "date-fns";
import isEmpty from "lodash/isEmpty";
import { useWindowDimensions } from "react-native";

import { DateFormat } from "@/constants/DateFormat";
import { Text } from "@/ui/core";
import SelectableCard from "@/ui/core/SelectableCard";
import { HStack, VStack } from "@/ui/layout";
import { LayoutToken } from "@/ui/theme/tokens";
import { formatISODate } from "@/utils/date.util";
import { joinTestIDs } from "@/utils/string.util";

interface BookingAvailabilityDayCardProps {
  day: string;
  onSelect: (timeSlotStartTime: string) => void;
  selectedTimeSlotStartTime?: string;
  timeSlots?: BaseTimeSlot[];
}

const BookingAvailabilityDayCard = ({
  day,
  onSelect,
  selectedTimeSlotStartTime,
  timeSlots,
}: BookingAvailabilityDayCardProps) => {
  const baseTestID = joinTestIDs(day, "availability-day-card");

  const date = parse(day, DateFormat.shortNumberMonthDayYear, new Date());

  const columnGap = 10;
  const rowWidth =
    useWindowDimensions().width -
    LayoutToken.ContentPaddingHorizontal * 2 -
    columnGap;
  const cardWidth = rowWidth / 2;

  if (isEmpty(timeSlots)) {
    return null;
  }
  return (
    <VStack gap={10} paddingBottom={16}>
      <Text
        accessibilityLabel={`${day} availability`}
        testID={joinTestIDs(baseTestID, "title")}
        variant="bodyMedium"
      >
        {format(date, DateFormat.weekDayMonthDay)}
      </Text>

      <HStack flexWrap="wrap" justifyContent="space-between" rowGap={columnGap}>
        {timeSlots?.map((timeSlot) => {
          const isSelected = timeSlot.start === selectedTimeSlotStartTime;

          const formattedStartTime = formatISODate({
            dateString: timeSlot.start,
            format: DateFormat.time,
          });

          return (
            <SelectableCard
              accessibilityLabel={`${day} ${timeSlot.start}`}
              isSelected={isSelected}
              key={timeSlot.start}
              label={formattedStartTime}
              maxWidth={cardWidth}
              onSelect={() => onSelect(timeSlot.start)}
              testID={joinTestIDs(baseTestID, "appointment-slot")}
            />
          );
        })}
      </HStack>
    </VStack>
  );
};

export type { BookingAvailabilityDayCardProps };
export default BookingAvailabilityDayCard;
